import { useState } from 'react';
import { MailIcon, AlertCircleIcon, CheckCircle2Icon, SendIcon } from 'lucide-react';
import type { ChangeRequestType, SubmitChangeRequestInput } from '@kennisbank/shared';
import { ApiError, apiPost } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * Inline practitioner-side feedback channel. Each surface (Kennisagent
 * answer card, Documenten row, Sociale Kaart provider card) embeds this
 * component so a user spotting a wrong author / outdated phone number /
 * miscited answer can flag it without leaving the page.
 *
 * Mechanism: POSTs a ChangeRequest to /api/v1/change-requests. It lands in
 * the SUPPOHRT-intern review queue (Beheer ⟶ Wijzigingsverzoeken), replacing
 * the earlier mailto-to-Linda placeholder. Voor Kennisagent-antwoorden draagt
 * het verzoek de sterbeoordeling (`answerRating`) plus de vrije toelichting,
 * waarmee de wachtrij tegelijk de eval-dataset is die corpus- en
 * prompt-tuning stuurt (ADR 0016).
 *
 * Varianten:
 *   - `WijzigingsverzoekLink` — kleine inline link in een rij/kaart. Gebruikt
 *     door Documenten + Sociale Kaart.
 *   - `WijzigingsverzoekModal` — het venster achter de link.
 *   - `buildPayload` — ook gebruikt door de inline beoordelingsstrook onder
 *     Kennisagent-antwoorden (`AnswerRating`), die zonder modal verstuurt.
 */

export type WijzigingsverzoekContext =
  | {
      surface: 'kennisagent';
      chatId: string | null;
      messageId?: string;
      query: string;
      answerExcerpt: string;
      citations: { chunkId: string; filename?: string; documentTitle?: string }[];
      /** Sterbeoordeling 1-5. Aanwezig = het sterrenpad: vrije tekst i.p.v. labelkeuze. */
      rating?: number;
      /** Berichten vóór de falende beurt (oudste eerst) — context bij feedback op een vervolgvraag. */
      priorMessages?: { role: 'user' | 'assistant'; content: string }[];
    }
  | {
      surface: 'documenten';
      filename: string;
      documentTitle: string;
      author: string;
      specialisations: string[];
    }
  | {
      surface: 'sociale-kaart';
      providerId: string;
      providerName: string;
      city: string | null;
    };

function surfaceLabel(s: WijzigingsverzoekContext['surface']): string {
  if (s === 'kennisagent') return 'Kennisagent';
  if (s === 'documenten') return 'Document';
  return 'Sociale Kaart';
}

function surfaceType(s: WijzigingsverzoekContext['surface']): ChangeRequestType {
  if (s === 'kennisagent') return 'antwoord_feedback';
  if (s === 'documenten') return 'fout_melden';
  return 'sociale_kaart_update';
}

function shortRef(ctx: WijzigingsverzoekContext): string {
  if (ctx.surface === 'kennisagent') {
    const t = ctx.query.trim();
    return t.length > 120 ? t.slice(0, 120) + '…' : t;
  }
  if (ctx.surface === 'documenten') return ctx.documentTitle || ctx.filename;
  return ctx.providerName;
}

/**
 * Human-readable context appended to the request description so the admin
 * sees provenance inline. Structured fields (originatingQuery, citedSources,
 * relatedProviderId) carry the same data machine-readably.
 */
function contextLines(ctx: WijzigingsverzoekContext): string[] {
  if (ctx.surface === 'documenten') {
    return [
      `Bestand: ${ctx.filename}`,
      `Titel: ${ctx.documentTitle}`,
      `Auteur: ${ctx.author || '(leeg)'}`,
      `Specialisaties: ${ctx.specialisations.length ? ctx.specialisations.join(', ') : '(geen)'}`,
    ];
  }
  if (ctx.surface === 'sociale-kaart') {
    return [
      `Provider: ${ctx.providerName}`,
      `Provider-ID: ${ctx.providerId}`,
      `Plaats: ${ctx.city ?? '(onbekend)'}`,
    ];
  }
  // kennisagent context is carried in structured fields; no text dump needed.
  return [];
}

export function buildPayload(
  ctx: WijzigingsverzoekContext,
  note: string,
): SubmitChangeRequestInput {
  const type = surfaceType(ctx.surface);
  const title = `${surfaceLabel(ctx.surface)}: ${shortRef(ctx)}`.slice(0, 200);
  const extra = contextLines(ctx);
  const description =
    [note.trim(), extra.length ? '\n' + extra.join('\n') : ''].join('').trim() ||
    '(geen toelichting)';

  if (ctx.surface === 'kennisagent') {
    const ratingLine = ctx.rating ? `Beoordeling: ${ctx.rating}/5` : null;
    // Full context in the request: the user's note + the actual answer Mila
    // gave, so the reviewer sees WHAT was wrong without opening the chat.
    // Question + sources ride along in the structured fields below.
    const kaDescription = [
      ...(ratingLine ? [ratingLine] : []),
      note.trim() || '(geen toelichting)',
      '--- Antwoord van Mila ---',
      ctx.answerExcerpt,
    ]
      .join('\n\n')
      .slice(0, 5000);
    return {
      type,
      title,
      description: kaDescription,
      ...(ctx.rating ? { answerRating: ctx.rating } : {}),
      originatingQuery: ctx.query.slice(0, 2000),
      // Embedded snapshot van het antwoord — de chat wordt na 30 dagen fysiek
      // gepurged (ADR 0008); zonder dit veld verliest de eval-dataset de tekst.
      ...(ctx.answerExcerpt ? { answerText: ctx.answerExcerpt.slice(0, 8000) } : {}),
      ...(ctx.chatId ? { chatId: ctx.chatId } : {}),
      ...(ctx.messageId ? { messageId: ctx.messageId } : {}),
      // Laatste 10 berichten vóór de falende beurt, zodat de reviewer (en de
      // eval-dataset) een vervolgvraag in context kan beoordelen.
      ...(ctx.priorMessages?.length
        ? {
            priorMessages: ctx.priorMessages.slice(-10).map((m) => ({
              role: m.role,
              content: m.content.slice(0, 8000),
            })),
          }
        : {}),
      citedSources: ctx.citations.slice(0, 20).map((c) => ({
        chunkId: c.chunkId.slice(0, 200),
        ...(c.filename ? { filename: c.filename.slice(0, 500) } : {}),
        ...(c.documentTitle ? { title: c.documentTitle.slice(0, 500) } : {}),
      })),
    };
  }
  if (ctx.surface === 'sociale-kaart') {
    return { type, title, description, relatedProviderId: ctx.providerId };
  }
  return { type, title, description };
}

/**
 * Standalone link variant for Documenten / Sociale Kaart. Click → modal
 * with textarea → "Versturen" posts the change request.
 */
export function WijzigingsverzoekLink({
  context,
  className = '',
}: {
  context: WijzigingsverzoekContext;
  className?: string;
}): JSX.Element | null {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  // Demo: POST /change-requests wordt door de read-only guard geblokkeerd,
  // dus de knop zou op een foutmelding doodlopen — niet tonen.
  if (user?.demo === true) return null;
  const label = context.surface === 'sociale-kaart' ? 'Klopt iets niet?' : 'Wijzigingsverzoek';
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={`inline-flex items-center gap-1 whitespace-nowrap text-[11px] text-ink-900/55 hover:text-brand-700 ${className}`}
      >
        <MailIcon size={11} aria-hidden="true" />
        {label}
      </button>
      {open && <WijzigingsverzoekModal context={context} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Modal — used directly by Kennisagent's thumbs-down promotion (where
 * we want a "Nee, alleen 👎" escape) and via WijzigingsverzoekLink for
 * the other two surfaces.
 *
 * onSubmitted vuurt alleen nadat de POST is geslaagd. De Kennisagent hangt
 * daar het vastleggen van de sterbeoordeling aan: annuleren of een mislukte
 * verzending mag geen beoordeling tonen die nooit is opgeslagen.
 */
export function WijzigingsverzoekModal({
  context,
  onClose,
  onSubmitted,
}: {
  context: WijzigingsverzoekContext;
  onClose: () => void;
  onSubmitted?: () => void;
}): JSX.Element {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const rating = context.surface === 'kennisagent' ? (context.rating ?? null) : null;
  const canSubmit = !submitting;

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiPost('/api/v1/change-requests', buildPayload(context, note));
      onSubmitted?.();
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Versturen mislukt (${err.status}). Probeer het opnieuw.`
          : 'Versturen mislukt. Probeer het opnieuw.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="mt-16 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wv-title"
      >
        {done ? (
          <>
            <h2
              id="wv-title"
              className="flex items-center gap-2 font-display text-lg font-bold text-ink-900"
            >
              <CheckCircle2Icon size={18} className="text-emerald-600" /> Bedankt voor je melding
            </h2>
            <p className="mt-2 text-sm text-ink-900/65">
              We hebben het doorgestuurd naar de beheerder van de Kennisbank. Je hoeft verder niets
              te doen.
            </p>
            <footer className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
              >
                Sluiten
              </button>
            </footer>
          </>
        ) : (
          <>
            <h2 id="wv-title" className="font-display text-lg font-bold text-ink-900">
              {rating !== null ? `Je gaf ${rating} van 5 sterren` : 'Wijzigingsverzoek versturen'}
            </h2>
            <p className="mt-1 text-sm text-ink-900/65">
              {rating !== null && rating <= 4
                ? 'Wat klopte er niet: de inhoud, de bron, of kon je er in de spreekkamer niets mee? Zonder toelichting kunnen we er niets mee.'
                : rating !== null
                  ? 'Waarom geef je deze beoordeling? Je toelichting is de manier waarop Mila beter wordt. Leeg laten mag ook.'
                  : 'Beschrijf kort wat er niet klopt of wat aangevuld zou moeten worden. We sturen het naar de beheerder van de Kennisbank.'}
            </p>

            {/* Direct open en met de cursor erin: bij vier sterren of minder zit
                de waarde in het waarom, en een klik extra is genoeg om die kwijt
                te raken. */}
            <textarea
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={rating !== null && rating <= 4 ? 5 : 4}
              placeholder={
                rating !== null
                  ? 'Wat ging er goed of mis aan dit antwoord?…'
                  : 'Beschrijf kort wat er niet klopt of wat aangevuld zou moeten worden…'
              }
              className="mt-4 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200/40"
            />

            <div className="mt-3 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
              <AlertCircleIcon size={13} className="mt-px flex-shrink-0" aria-hidden="true" />
              <span>
                Geen spoed of klachten over zorgverlening — daarvoor: huisarts of
                klachtenfunctionaris.
              </span>
            </div>

            {error && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-red-700">
                <AlertCircleIcon size={13} /> {error}
              </p>
            )}

            <footer className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-3 py-1.5 text-sm text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
              >
                Annuleer
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                <SendIcon size={13} />
                {submitting ? 'Versturen…' : 'Versturen'}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
